'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, type RefObject } from 'react';
import { animate, useMotionValue } from 'motion/react';

// Copied from the demo app's `springs.snappy` so the spring-back matches its feel.
const SNAPPY_SPRING = {
  type: 'spring',
  stiffness: 400,
  damping: 28,
  mass: 0.8
} as const;

// How far the strip can be pulled past an edge before resistance maxes out.
const MAX_PULL = 120;
function rubberBand(delta: number) {
  const distance = Math.abs(delta);
  const damped = (1 - 1 / (distance / MAX_PULL + 1)) * MAX_PULL;
  return Math.sign(delta) * damped;
}
export function useOverscrollBounce(scrollRef, enabled) {
  const $ = _c(5);
  const x = useMotionValue(0);
  const startXRef = useRef(0);
  const pullingRef = useRef(false);
  let t0;
  let t1;
  if ($[0] !== enabled || $[1] !== scrollRef || $[2] !== x) {
    t0 = () => {
      const scroller = scrollRef.current;
      if (!scroller || !enabled) {
        return;
      }
      const touchStartHandler = function touchStartHandler(event) {
        const touch = event.touches[0];
        if (!touch) {
          return;
        }
        startXRef.current = touch.clientX;
        pullingRef.current = false;
      };
      const touchMoveHandler = function touchMoveHandler(event_0) {
        const node = scrollRef.current;
        const touch_0 = event_0.touches[0];
        if (!node || !touch_0) {
          return;
        }
        const delta = touch_0.clientX - startXRef.current;
        const atStart = node.scrollLeft <= 0;
        const atEnd = node.scrollLeft + node.clientWidth >= node.scrollWidth - 1;
        const pullingRight = atStart && delta > 0;
        const pullingLeft = atEnd && delta < 0;
        if (!(pullingRight || pullingLeft)) {
          if (pullingRef.current) {
            pullingRef.current = false;
            x.set(0);
          }
          return;
        }
        if (!event_0.cancelable) {
          return;
        }
        pullingRef.current = true;
        event_0.preventDefault();
        x.set(rubberBand(delta));
      };
      const releaseHandler = function releaseHandler() {
        if (!pullingRef.current) {
          return;
        }
        pullingRef.current = false;
        animate(x, 0, SNAPPY_SPRING);
      };
      scroller.addEventListener("touchstart", touchStartHandler, {
        passive: true
      });
      scroller.addEventListener("touchmove", touchMoveHandler, {
        passive: false
      });
      scroller.addEventListener("touchend", releaseHandler, {
        passive: true
      });
      scroller.addEventListener("touchcancel", releaseHandler, {
        passive: true
      });
      return () => {
        scroller.removeEventListener("touchstart", touchStartHandler);
        scroller.removeEventListener("touchmove", touchMoveHandler);
        scroller.removeEventListener("touchend", releaseHandler);
        scroller.removeEventListener("touchcancel", releaseHandler);
      };
    };
    t1 = [scrollRef, enabled, x];
    $[0] = enabled;
    $[1] = scrollRef;
    $[2] = x;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return x;
}